import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useQueryClient } from '@tanstack/react-query';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Package, Users, FolderKanban, Gauge, HardDrive, FileUp, FileDown, MessageSquare, Zap, TrendingUp, AlertTriangle, CheckCircle, Calendar, BarChart3, FileText, Receipt } from 'lucide-react';
import { get_all_case_types, get_cabinet_case_types, add_cabinet_case_type, remove_cabinet_case_type, get_all_roles, get_cabinet_usage_current, get_cabinet_usage_monthly, list_cabinet_invoices } from '@/api/cabinet';
import { invoicingService } from '@/api/invoicing';
import type { CabinetInvoicingSettings } from '@/api/invoicing';
import { useToast } from '@/hooks/use-toast';
import type { CaseType } from '@/views/cases/data/schema';
import type { Role } from '@/views/users/data/schema';
import type { CabinetUsageCurrent, MonthlyUsage } from '@/types/subscription';
import type { CabinetInvoice } from '@/types/subscription';

const EMPTY_SETTINGS: Partial<CabinetInvoicingSettings> = {
  address: '', phone: '', email: '',
  ice: '', if_number: '', rc: '', patente: '', cnss: '',
  tva_enabled: false, tva_number: '', default_tva_rate: '',
  invoice_prefix: 'F',
  bank_name: '', bank_rib: '', bank_iban: '',
  default_payment_terms_days: 30,
  invoice_footer: '',
}

export default function CabinetSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [invSettings, setInvSettings] = useState<Partial<CabinetInvoicingSettings>>(EMPTY_SETTINGS);
  const [invSettingsLoading, setInvSettingsLoading] = useState(true);
  const [invSettingsSaving, setInvSettingsSaving] = useState(false);
  const [invNextNumber, setInvNextNumber] = useState<number | null>(null);

  useEffect(() => {
    invoicingService.getSettings()
      .then((data) => {
        setInvSettings(data);
        setInvNextNumber(data.next_invoice_number);
      })
      .catch(() => {})
      .finally(() => setInvSettingsLoading(false));
  }, []);

  const handleInvSettingsSave = async () => {
    setInvSettingsSaving(true);
    try {
      const { id, next_invoice_number, created_at, updated_at, ...writable } = invSettings as CabinetInvoicingSettings;
      const updated = await invoicingService.updateSettings(writable);
      setInvSettings(updated);
      setInvNextNumber(updated.next_invoice_number);
      toast({ title: t('inv_settings_saved') });
    } catch {
      toast({ title: t('error'), variant: 'destructive' });
    } finally {
      setInvSettingsSaving(false);
    }
  };

  const setInv = (field: keyof CabinetInvoicingSettings, value: string | boolean | number) => {
    setInvSettings((prev) => ({ ...prev, [field]: value }));
  };

  const [allCaseTypes, setAllCaseTypes] = useState<CaseType[]>([]);
  const [cabinetCaseTypes, setCabinetCaseTypes] = useState<CaseType[]>([]);
  const [caseTypeSearch, setCaseTypeSearch] = useState('');

  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [roleSearch, setRoleSearch] = useState('');

  const [usage, setUsage] = useState<CabinetUsageCurrent | null>(null);
  const [loadingUsage, setLoadingUsage] = useState<boolean>(true);

  const [monthlyUsage, setMonthlyUsage] = useState<MonthlyUsage[]>([]);
  const [loadingMonthly, setLoadingMonthly] = useState<boolean>(true);
  const [showMonthlyDetails, setShowMonthlyDetails] = useState<boolean>(false);

  const [invoices, setInvoices] = useState<CabinetInvoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      const [allCT, cabCT, allR] = await Promise.all([
        get_all_case_types(),
        get_cabinet_case_types(),
        get_all_roles(),
      ]);
      setAllCaseTypes(allCT);
      setCabinetCaseTypes(cabCT);
      setAllRoles(allR);
    };
    init();
  }, []);

  useEffect(() => {
    const loadUsage = async () => {
      try {
        setLoadingUsage(true);
        const data = await get_cabinet_usage_current();
        setUsage(data);
      } catch (e) {
        // noop: display nothing on error
      } finally {
        setLoadingUsage(false);
      }
    };
    loadUsage();
  }, []);

  useEffect(() => {
    const loadMonthlyUsage = async () => {
      try {
        setLoadingMonthly(true);
        const data = await get_cabinet_usage_monthly();
        setMonthlyUsage(data);
      } catch (e) {
        // noop: display nothing on error
      } finally {
        setLoadingMonthly(false);
      }
    };
    loadMonthlyUsage();
  }, []);

  useEffect(() => {
    const loadInvoices = async () => {
      try {
        setLoadingInvoices(true);
        const data = await list_cabinet_invoices();
        setInvoices(Array.isArray(data) ? data : []);
      } catch (e) {
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };
    loadInvoices();
  }, []);

  const filteredCaseTypes = useMemo(() => {
    return allCaseTypes.filter((ct) => ct.name.toLowerCase().includes(caseTypeSearch.toLowerCase()));
  }, [allCaseTypes, caseTypeSearch]);

  const filteredRoles = useMemo(() => {
    return allRoles.filter((r) => r.name.toLowerCase().includes(roleSearch.toLowerCase()));
  }, [allRoles, roleSearch]);

  // Optimistic handlers for instant UI updates
  const handleAddCaseType = async (caseType: CaseType) => {
    // Optimistically update UI immediately
    setCabinetCaseTypes(prev => [...prev, caseType]);
    try {
      await add_cabinet_case_type(caseType.id);
    } catch (error) {
      // Revert on error
      setCabinetCaseTypes(prev => prev.filter(ct => ct.id !== caseType.id));
    }
  };

  const handleRemoveCaseType = async (caseType: CaseType) => {
    // Optimistically update UI immediately
    setCabinetCaseTypes(prev => prev.filter(ct => ct.id !== caseType.id));
    try {
      await remove_cabinet_case_type(caseType.id);
    } catch (error) {
      // Revert on error
      setCabinetCaseTypes(prev => [...prev, caseType]);
    }
  };



  const currentMonthUsage = useMemo(() => {
    const now = new Date();
    return monthlyUsage.find(m => m.year === now.getFullYear() && m.month === now.getMonth() + 1);
  }, [monthlyUsage]);

  const pct = (used: number, max: number) => {
    if (!max || max <= 0) return 0;
    return Math.min(100, Math.round((used / max) * 100));
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${sizes[i]}`;
  };

  const formatCredits = (credits: string | number) => {
    const num = typeof credits === 'string' ? parseFloat(credits) : credits;
    return num.toFixed(2);
  };

  const getMonthName = (month: number) => {
    const months = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
    return months[month - 1] || month.toString();
  };

  const formatMoney = (cents: number, currency: string) => {
    const amount = (cents || 0) / 100;
    return `${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  };

  const sanitizeUrl = (url: string) => url?.replace(/`/g, '').trim();

  const formatDate = (epochSeconds: number) => {
    try {
      return new Date(epochSeconds * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return `${epochSeconds}`;
    }
  };

  return (
    <div className="space-y-8">
      {/* Plan & Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t('cabinet.settings.plan_usage', 'الخطة والاستخدام')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingUsage ? (
            <div className="text-sm text-muted-foreground">{t('loading', 'جاري التحميل...')}</div>
          ) : usage ? (
            <div className="space-y-6">
              {/* Plan badge and tokens */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="inline-flex items-center gap-2 rounded-md border px-3 py-1 text-sm">
                  <Package className="h-4 w-4" />
                  <span>{t('cabinet.plan', 'الخطة')}:</span>
                  <span className="font-medium">{usage.plan_code}</span>
                </div>
                <div className="inline-flex items-center gap-2 rounded-md border px-3 py-1 text-sm">
                  <Gauge className="h-4 w-4" />
                  <span>{t('cabinet.month_tokens_used', 'رموز الشهر المستخدمة')}:</span>
                  <span className="font-medium">{usage.month_tokens_used}</span>
                </div>
              </div>

              {/* Grid metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {/* Users */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4" />
                      <span>{t('cabinet.users', 'المستخدمون')}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{usage.users_count}</span>
                      <span className="text-muted-foreground"> / {usage.max_users}</span>
                    </div>
                  </div>
                  <div className="h-2 w-full rounded bg-muted">
                    <div
                      className="h-2 rounded bg-primary"
                      style={{ width: `${pct(usage.users_count, usage.max_users)}%` }}
                    />
                  </div>
                </div>

                {/* Cases */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <FolderKanban className="h-4 w-4" />
                      <span>{t('cabinet.cases', 'الملفات')}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{usage.cases_count}</span>
                      <span className="text-muted-foreground"> / {usage.max_cases}</span>
                    </div>
                  </div>
                  <div className="h-2 w-full rounded bg-muted">
                    <div
                      className="h-2 rounded bg-primary"
                      style={{ width: `${pct(usage.cases_count, usage.max_cases)}%` }}
                    />
                  </div>
                </div>

                {/* Active Cases */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <FolderKanban className="h-4 w-4" />
                      <span>{t('cabinet.active_cases', 'الملفات النشطة')}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{usage.active_cases_count}</span>
                      <span className="text-muted-foreground"> / {usage.max_active_cases}</span>
                    </div>
                  </div>
                  <div className="h-2 w-full rounded bg-muted">
                    <div
                      className="h-2 rounded bg-primary"
                      style={{ width: `${pct(usage.active_cases_count, usage.max_active_cases)}%` }}
                    />
                  </div>
                </div>

                {/* Storage */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <HardDrive className="h-4 w-4" />
                      <span>{t('cabinet.storage', 'التخزين')}</span>
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">{formatBytes(usage.storage_bytes)}</span>
                      <span className="text-muted-foreground"> / {usage.max_storage_mb} MB</span>
                    </div>
                  </div>
                  <div className="h-2 w-full rounded bg-muted">
                    <div
                      className="h-2 rounded bg-primary"
                      style={{
                        width: `${pct(usage.storage_bytes / (1024 * 1024), usage.max_storage_mb)}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Month Storage Uploaded */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <FileUp className="h-4 w-4" />
                    <span>{t('cabinet.month_storage_uploaded', 'مساحة الشهر المرفوعة')}</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">{formatBytes(usage.month_storage_uploaded_bytes)}</span>
                  </div>
                </div>

                {/* Month Storage Deleted */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <FileDown className="h-4 w-4" />
                    <span>{t('cabinet.month_storage_deleted', 'مساحة الشهر المحذوفة')}</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">{formatBytes(usage.month_storage_deleted_bytes)}</span>
                  </div>
                </div>
              </div>

              {/* Monthly Details Toggle */}
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowMonthlyDetails(!showMonthlyDetails)}
                  className="flex items-center gap-2"
                >
                  <BarChart3 className="h-4 w-4" />
                  {showMonthlyDetails
                    ? t('cabinet.hide_monthly_details', 'إخفاء التفاصيل الشهرية')
                    : t('cabinet.show_monthly_details', 'عرض التفاصيل الشهرية')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">{t('no_data', 'لا توجد بيانات')}</div>
          )}
        </CardContent>
      </Card>

      {/* Monthly Usage Analytics */}
      {showMonthlyDetails && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {t('cabinet.monthly_analytics', 'التحليلات الشهرية')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingMonthly ? (
              <div className="text-sm text-muted-foreground">{t('loading', 'جاري التحميل...')}</div>
            ) : currentMonthUsage ? (
              <div className="space-y-8">
                {/* Current Month Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                        <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">إجمالي الرسائل</div>
                        <div className="text-2xl font-bold">{currentMonthUsage.total_messages}</div>
                        {/* Detailed breakdown per channel not available in MonthlyUsage type */}
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                        <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">الرصيد المستخدم</div>
                        <div className="text-2xl font-bold">{formatCredits(currentMonthUsage.ai_credits_used)}</div>
                        <div className="text-xs text-muted-foreground">
                          {currentMonthUsage.monthly_credit_limit ? `من ${formatCredits(currentMonthUsage.monthly_credit_limit)}` : 'بلا حدود'}
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                        <Gauge className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">الرموز المستخدمة</div>
                        <div className="text-2xl font-bold">{currentMonthUsage.tokens_used.toLocaleString()}</div>
                        <div className="text-xs text-muted-foreground">
                          دخل: {currentMonthUsage.input_tokens.toLocaleString()} | خرج: {currentMonthUsage.output_tokens.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${currentMonthUsage.is_quota_exceeded ? 'bg-red-100 dark:bg-red-900' : 'bg-green-100 dark:bg-green-900'}`}>
                        {currentMonthUsage.is_quota_exceeded ? (
                          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        ) : (
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">نسبة الاستخدام</div>
                        <div className="text-2xl font-bold">{currentMonthUsage.usage_percentage.toFixed(1)}%</div>
                        <div className="text-xs text-muted-foreground">
                          {currentMonthUsage.is_quota_exceeded ? 'تم تجاوز الحد' : 'ضمن الحد المسموح'}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Usage Progress Bar */}
                {currentMonthUsage.monthly_credit_limit && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">استخدام الرصيد الشهري</span>
                      <span className="text-sm text-muted-foreground">
                        {formatCredits(currentMonthUsage.ai_credits_used)} / {formatCredits(currentMonthUsage.monthly_credit_limit)}
                      </span>
                    </div>
                    <div className="h-3 w-full rounded bg-muted">
                      <div
                        className={`h-3 rounded transition-all ${currentMonthUsage.is_quota_exceeded ? 'bg-red-500' : 'bg-primary'
                          }`}
                        style={{ width: `${Math.min(100, currentMonthUsage.usage_percentage)}%` }}
                      />
                    </div>
                  </div>
                )}

                <Separator />

                {/* Detailed Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {/* Performance Metrics */}
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      مقاييس الأداء
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm">الطلبات الناجحة</span>
                        <span className="font-medium">{currentMonthUsage.successful_requests}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">الأخطاء</span>
                        <span className="font-medium">{currentMonthUsage.failed_requests}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">معدل النجاح</span>
                        <span className="font-medium">{
                          (() => {
                            const success = currentMonthUsage.successful_requests || 0;
                            const fail = currentMonthUsage.failed_requests || 0;
                            const total = success + fail;
                            const rate = total > 0 ? (success / total) * 100 : 0;
                            return rate.toFixed(1);
                          })()
                        }%</span>
                      </div>
                      {/* Average response time not available in MonthlyUsage */}
                    </div>
                  </div>

                  {/* Activity Metrics (monthly deltas not available in MonthlyUsage) */}

                  {/* Storage Metrics */}
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <HardDrive className="h-4 w-4" />
                      التخزين الشهري
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm">مرفوع</span>
                        <span className="font-medium">{formatBytes(usage?.month_storage_uploaded_bytes || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">محذوف</span>
                        <span className="font-medium">{formatBytes(usage?.month_storage_deleted_bytes || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">صافي التغيير</span>
                        <span className="font-medium">{
                          (() => {
                            const uploaded = usage?.month_storage_uploaded_bytes || 0;
                            const deleted = usage?.month_storage_deleted_bytes || 0;
                            const net = uploaded - deleted;
                            const sign = net >= 0 ? '+' : '-';
                            return `${sign}${formatBytes(Math.abs(net))}`;
                          })()
                        }</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">التخزين الحالي</span>
                        <span className="font-medium">{formatBytes(usage?.storage_bytes || 0)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Historical Data */}
                {monthlyUsage.length > 1 && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        البيانات التاريخية
                      </h4>
                      <div className="grid gap-4">
                        {monthlyUsage.slice(0, 6).map((month) => (
                          <Card key={month.id} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Badge variant="outline">
                                  {getMonthName(month.month)} {month.year}
                                </Badge>
                                <div className="text-sm text-muted-foreground">
                                  {month.total_messages} رسالة | {formatCredits(month.ai_credits_used)} رصيد
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {month.is_quota_exceeded ? (
                                  <Badge variant="destructive">تجاوز الحد</Badge>
                                ) : (
                                  <Badge variant="secondary">{month.usage_percentage.toFixed(1)}%</Badge>
                                )}
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">{t('no_monthly_data', 'لا توجد بيانات شهرية')}</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Billing & Invoices */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('cabinet.billing_invoices', 'الفوترة والفواتير')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingInvoices ? (
            <div className="text-sm text-muted-foreground">{t('loading', 'جاري التحميل...')}</div>
          ) : invoices.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t('no_invoices', 'لا توجد فواتير')}</div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                <div className="grid grid-cols-12 gap-2 px-2 py-2 text-xs text-muted-foreground">
                  <div className="col-span-3">{t('invoice.number', 'رقم الفاتورة')}</div>
                  <div className="col-span-2">{t('invoice.issue_date', 'تاريخ الإصدار')}</div>
                  <div className="col-span-2">{t('invoice.status', 'الحالة')}</div>
                  <div className="col-span-3">{t('invoice.total', 'الإجمالي')}</div>
                  <div className="col-span-2">{t('invoice.actions', 'إجراءات')}</div>
                </div>
                <Separator />
                <div className="divide-y">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="grid grid-cols-12 gap-2 px-2 py-3 items-center">
                      <div className="col-span-3 font-mono text-sm">{inv.number}</div>
                      <div className="col-span-2 text-sm">{formatDate(inv.issue_date)}</div>
                      <div className="col-span-2">
                        <Badge variant={inv.status === 'issued' ? 'secondary' : inv.status === 'paid' ? 'default' : inv.status === 'void' ? 'destructive' : 'outline'}>
                          {inv.status}
                        </Badge>
                      </div>
                      <div className="col-span-3 text-sm font-medium">{formatMoney(inv.total_cents, inv.currency)}</div>
                      <div className="col-span-2">
                        {inv.pdf_url ? (
                          <a
                            href={sanitizeUrl(inv.pdf_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                          >
                            <FileText className="h-4 w-4" />
                            {t('invoice.download_pdf', 'تحميل PDF')}
                          </a>
                        ) : (
                          <span className="text-sm text-muted-foreground">{t('invoice.no_pdf', 'لا يوجد رابط')}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoicing Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            {t('inv_settings_title')}
          </CardTitle>
          <CardDescription>{t('inv_settings_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {invSettingsLoading ? (
            <div className="text-sm text-muted-foreground">{t('loading')}</div>
          ) : (
            <div className="space-y-6">

              {/* Cabinet Identity */}
              <div>
                <h4 className="text-sm font-semibold mb-3">{t('inv_settings_identity')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_address')}</Label>
                    <Input value={invSettings.address ?? ''} onChange={(e) => setInv('address', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_phone')}</Label>
                    <Input value={invSettings.phone ?? ''} onChange={(e) => setInv('phone', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_email')}</Label>
                    <Input type="email" value={invSettings.email ?? ''} onChange={(e) => setInv('email', e.target.value)} />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Legal Identifiers */}
              <div>
                <h4 className="text-sm font-semibold mb-3">{t('inv_settings_legal')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_ice')}</Label>
                    <Input value={invSettings.ice ?? ''} onChange={(e) => setInv('ice', e.target.value)} placeholder="002345678000012" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_if')}</Label>
                    <Input value={invSettings.if_number ?? ''} onChange={(e) => setInv('if_number', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_rc')}</Label>
                    <Input value={invSettings.rc ?? ''} onChange={(e) => setInv('rc', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_patente')}</Label>
                    <Input value={invSettings.patente ?? ''} onChange={(e) => setInv('patente', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_cnss')}</Label>
                    <Input value={invSettings.cnss ?? ''} onChange={(e) => setInv('cnss', e.target.value)} />
                  </div>
                </div>
              </div>

              <Separator />

              {/* TVA */}
              <div>
                <h4 className="text-sm font-semibold mb-3">{t('inv_settings_tva')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div className="flex items-center gap-3 pt-4">
                    <Switch
                      checked={!!invSettings.tva_enabled}
                      onCheckedChange={(v) => setInv('tva_enabled', v)}
                    />
                    <Label className="text-sm">{t('inv_settings_tva_enabled')}</Label>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_tva_number')}</Label>
                    <Input
                      value={invSettings.tva_number ?? ''}
                      onChange={(e) => setInv('tva_number', e.target.value)}
                      disabled={!invSettings.tva_enabled}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_tva_rate')}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={invSettings.default_tva_rate ?? ''}
                      onChange={(e) => setInv('default_tva_rate', e.target.value)}
                      disabled={!invSettings.tva_enabled}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Invoice Numbering */}
              <div>
                <h4 className="text-sm font-semibold mb-3">{t('inv_settings_numbering')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_prefix')}</Label>
                    <Input
                      value={invSettings.invoice_prefix ?? ''}
                      onChange={(e) => setInv('invoice_prefix', e.target.value)}
                      maxLength={10}
                      placeholder="F"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_next_number')}</Label>
                    <Input
                      value={invNextNumber ?? ''}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_payment_terms')}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={invSettings.default_payment_terms_days ?? 30}
                      onChange={(e) => setInv('default_payment_terms_days', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Bank */}
              <div>
                <h4 className="text-sm font-semibold mb-3">{t('inv_settings_bank')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_bank_name')}</Label>
                    <Input value={invSettings.bank_name ?? ''} onChange={(e) => setInv('bank_name', e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_bank_rib')}</Label>
                    <Input value={invSettings.bank_rib ?? ''} onChange={(e) => setInv('bank_rib', e.target.value)} placeholder="007 780 0001234567890012 26" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('inv_settings_bank_iban')}</Label>
                    <Input value={invSettings.bank_iban ?? ''} onChange={(e) => setInv('bank_iban', e.target.value)} />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Footer */}
              <div className="space-y-1">
                <Label className="text-xs">{t('inv_settings_footer')}</Label>
                <Textarea
                  value={invSettings.invoice_footer ?? ''}
                  onChange={(e) => setInv('invoice_footer', e.target.value)}
                  rows={3}
                  placeholder="Merci pour votre confiance."
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={handleInvSettingsSave} disabled={invSettingsSaving}>
                  {invSettingsSaving ? t('loading') : t('inv_settings_save')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Case Types Management */}
      <Card>
        <CardHeader>
          <CardTitle>{t('cabinet.case_types', 'أنواع القضايا')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              placeholder={t('search', 'بحث')}
              value={caseTypeSearch}
              onChange={(e) => setCaseTypeSearch(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {filteredCaseTypes.map((ct) => {
              const added = cabinetCaseTypes.some((c) => c.id === ct.id);
              return (
                <div key={ct.id} className="flex items-center justify-between rounded border p-2">
                  <div className="text-sm">{ct.name}</div>
                  {added ? (
                    <Button variant="destructive" size="sm" onClick={() => handleRemoveCaseType(ct)}>
                      {t('remove', 'إزالة')}
                    </Button>
                  ) : (
                    <Button variant="default" size="sm" onClick={() => handleAddCaseType(ct)}>
                      {t('add', 'إضافة')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Roles Management */}
      <Card>
        <CardHeader>
          <CardTitle>{t('cabinet.roles', 'الأدوار')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              placeholder={t('search', 'بحث')}
              value={roleSearch}
              onChange={(e) => setRoleSearch(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Array.from(new Map(filteredRoles.map(role => [role.id, role])).values()).map((role) => {
              const isActive = role.is_enabled;
              return (
                <div key={role.id} className="flex items-center justify-between rounded border p-2">
                  <div className="text-sm">{role.name}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{isActive ? t('enabled', 'مفعل') : t('disabled', 'معطل')}</span>
                    <Switch
                      checked={!!isActive}
                      onCheckedChange={async (checked) => {
                        // Optimistic update
                        const updatedRoles = allRoles.map(r =>
                          r.id === role.id ? { ...r, is_enabled: checked } : r
                        );
                        setAllRoles(updatedRoles);

                        try {
                          const { toggle_role_status } = await import('@/api/roles');
                          await toggle_role_status(role.id);
                          queryClient.invalidateQueries({ queryKey: ['roles'] });
                        } catch (error) {
                          // Revert on error
                          setAllRoles(allRoles);
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
